import React from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled'
import { ArticleOutlined } from '@mui/icons-material'
import aplicacionWebImg from '../../assets/images/publicaciones/articulos/aplicacion-web.webp'
import arboladoUrbanoLojaImg from '../../assets/images/publicaciones/articulos/latitud-0.webp'
import arbolado_urbano_lojaImg from '../../assets/images/publicaciones/articulos/arbolado-loja.webp'

// Datos de los artículos
const articlesData = [
  {
    title: 'Servicios ecosistémicos del arbolado urbano en la ciudad de Loja, Ecuador',
    authors: 'Lozano, D., Macas, M. F., Jumbo, N., Fernández, P., Pucha, D.',
    date: '2025',
    link: 'https://doi.org/10.54753/cedamaz.v15i1.2374',
    image: arbolado_urbano_lojaImg,
    journal: 'CEDAMAZ',
    volume: 'Vol. 15, Núm. 1',
    pages: '8–18',
    keywords: [
      'Arbolado urbano',
      'Servicios ecosistémicos',
      'Calidad del aire',
      'Almacenamiento de carbono',
      'Loja',
    ],
    description:
      'Estudio que evalúa cinco servicios ecosistémicos proporcionados por el arbolado urbano de la ciudad de Loja (almacenamiento de carbono, producción de oxígeno, hábitat y refugio, recreación y calidad del aire) mediante inventario forestal y análisis funcional ecológico en parques y avenidas.',
    researchArea: 'Evaluación de Servicios Ecosistémicos Urbanos',
    methodology:
      'Inventario forestal urbano y valoración de servicios ecosistémicos mediante métodos cuantitativos y entrevistas para recreación',
    contribution:
      'Proporciona información científica para la gestión sostenible del arbolado urbano y la planificación de políticas ambientales en Loja',
  },
  {
    title:
      'Caracterización florística y estructura del arbolado urbano de la ciudad de Loja',
    authors:
      'Merino, B., Gualán, R., Macas, M. F., Armijos, A., Fernández, P., Jumbo, N., … Pucha-Cofrep.',
    date: '2023',
    link: 'https://doi.org/10.54753/blc.v13i2.1886',
    image: arboladoUrbanoLojaImg,
    journal: 'Bosques Latitud Cero',
    volume: 'Vol. 13, Núm. 2',
    pages: '1-22',
    keywords: [
      'Arbolado urbano',
      'Composición florística',
      'Estructura',
      'Diversidad',
      'Loja',
    ],
    description:
      'Estudio completo de la composición florística y estructura del arbolado urbano de Loja, analizando diversidad de especies, distribución espacial, parámetros dasométricos y estado fitosanitario de los árboles urbanos.',
    researchArea: 'Estudios de la Composición Florística Urbana',
    methodology: 'Inventario forestal urbano con análisis de diversidad y estructura',
    contribution:
      'Proporciona base científica para la gestión sostenible del patrimonio arbóreo urbano de Loja',
  },
  {
    title: 'Aplicación web para la gestión del arbolado urbano de la ciudad de Loja',
    // authors: 'Johnny Fabián González Guamán, Wilman Chamba-Zaragocin, Oscar M. Cumbicus-Pineda',
    authors: 'González Guamán, J. F., Chamba-Zaragocin, W., & Cumbicus-Pineda, O. M.',
    date: '2023',
    link: 'https://doi.org/10.54753/cedamaz.v13i1.1933',
    image: aplicacionWebImg,
    journal: 'CEDAMAZ',
    volume: 'Vol. 13, Núm. 1',
    keywords: ['ICONIX', 'Aplicación Web', 'Arbolado urbano', 'NodeJS', 'ReactJS'],
    description:
      'Desarrollo de una aplicación web especializada para la gestión eficiente de datos científicos y técnicos del arbolado urbano, implementando tecnologías modernas para facilitar la investigación y toma de decisiones.',
    methodology: 'ICONIX con arquitectura n-tier, modelos UML',
    technologies: [
      'JavaScript',
      'React',
      'Mapbox',
      'Node.js',
      'Express',
      'Sequelize',
      'MySQL',
    ],
    testing: 'Pruebas de caja negra, pruebas funcionales, pruebas API-REST',
    contribution:
      'Herramienta tecnológica integral para la gestión del patrimonio arbóreo urbano de Loja',
    projectContext:
      "Proyecto de investigación forestal 'Dinámica de crecimiento y servicios ecosistémicos del arbolado urbano de la ciudad de Loja'",
  },
]

const ArticlesSection = () => {
  const theme = useTheme()

  return (
    <Box>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          borderLeft: `4px solid ${theme.palette.secondary.main}`,
          pl: 2,
          mt: 8,
          mb: 4,
        }}>
        <Typography
          variant="h4"
          gutterBottom
          fontWeight={600}
          sx={{ color: theme.palette.text.forestGreen, my: 0 }}>
          Artículos
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {articlesData.map((article, idx) => (
          <Grid item xs={12} md={4} my={2} key={idx}>
            <Card
              component="a"
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                boxShadow: theme.shadows[3],
                border: `1px solid ${theme.palette.background.default}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  color: theme.palette.text.primary,
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                },
                '&:hover img': {
                  transform: 'scale(1.05)',
                },
              }}>
              <Box sx={{ position: 'relative', p: { xs: 3, md: 4 } }}>
                {/* Título del artículo */}
                <Box mb={2}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minHeight: { xs: '3.6em', md: '3.9em' },
                      fontSize: { xs: '1rem', md: '1.25rem' },
                      '&:hover': {
                        color: theme.palette.secondary.main,
                      },
                    }}>
                    {article.title}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2, bgcolor: theme.palette.primary.main }} />

                {/* Imagen del artículo */}
                <Box
                  height={{ xs: 200, md: 220 }}
                  overflow="hidden"
                  sx={{
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                    mb: 2,
                  }}>
                  <CardMedia
                    component="img"
                    image={article.image}
                    alt={article.title}
                    sx={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                      boxShadow:
                        '0px 2px 8px rgba(0, 0, 0, 0.25), 0px 4px 6px rgba(0, 0, 0, 0.22)',
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  />
                </Box>

                <CardContent sx={{ px: 0, py: 0, flex: 1 }}>
                  {/* Revista */}
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <ArticleOutlined
                      sx={{
                        fontSize: 16,
                        color: theme.palette.text.mutedTeal || '#4DB6AC',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.forestGreen || '#2E7D32',
                        fontWeight: 500,
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                      }}>
                      {article.journal}
                    </Typography>
                  </Box>

                  {/* Fecha */}
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <AccessTimeFilledIcon
                      sx={{
                        fontSize: 16,
                        color: theme.palette.text.mutedTeal || '#4DB6AC',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.forestGreen || '#2E7D32',
                        fontSize: { xs: '0.8rem', md: '0.875rem' },
                      }}>
                      {article.date}
                    </Typography>
                  </Box>

                  {/* Descripción */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 2,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: { xs: 2, md: 3 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      lineHeight: 1.4,
                    }}>
                    {article.description}
                  </Typography>

                  {/* Autores */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', md: '0.8rem' },
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    {article.authors}
                  </Typography>
                </CardContent>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default ArticlesSection
